/**
 * AdoraCare - Lead-to-Appointment cockpit view model
 * (LEAD-TO-APPOINTMENT-COCKPIT-V1).
 *
 * Pure, React-free helpers + types + the role-only access decision for the
 * agent/COC lead-to-appointment operating cockpit. Kept side-effect-free (no
 * React, no CSS, no api import at runtime - only `import type` and the pure
 * workboard-view + datetime-ist helpers) so the vitest suite can exercise every
 * helper by direct unit call and render the presentational component via
 * react-dom/server (this repo has NO jsdom; see LeadToAppointmentCockpit.test.tsx).
 *
 * WHAT THIS COCKPIT COMPOSES (no new backend; every affordance is an existing
 * client fn):
 *   - the always-on caller work-queue read GET /api/me/work-actions
 *     (fetchMyWorkActions) as the LEAD WORKLIST - the leads with an open
 *     follow-up assigned to the caller. That read carries the carved lead NAME
 *     and the owning leadId, but its `channel`/`dueContext` are FIXED first-slice
 *     constants ('Call'/'Assigned'), so this cockpit NEVER renders them as a real
 *     source/channel.
 *   - the role-scoped lead directory GET /api/leads/workspace (fetchLeadsWorkspace)
 *     as the ENRICHMENT for the selected lead: the real source
 *     (sourceNormalized/utm) and the administrative appointment section
 *     (status/time/doctor-assigned-boolean). Gated by the audience signal
 *     me.leadsWorkspaceEnabled (the internal-surface env flag was retired); when
 *     absent the enrichment is honestly OMITTED, never faked - the cockpit still
 *     functions on the always-on worklist read.
 *   - book an appointment via POST /api/appointments/booked (bookAppointment) -
 *     a chosen IST time only. Gated by the audience signal
 *     me.crmAppointmentWorkbenchEnabled (the internal-surface env flag was
 *     retired); fail-closed disabled when absent.
 *   - move the lead stage via POST /api/leads/:id/stage (advanceLeadStage,
 *     FRONT-HALF targets only - reused from the Workboard client).
 *   - clear a follow-up via POST /api/work-actions/:id/transition
 *     (transitionWorkAction) - reused from #183.
 *
 * HONEST CEILING (the hard line): booking is PARTIAL. AdoraCare books an
 * appointment at a chosen time with NO real open-slot list and NO doctor
 * assignment (the create route ignores any slot id and always resolves doctor_id
 * to null; there is no slot-listing endpoint in the web stack). So this cockpit
 * renders a plain date + hour + 15-minute picker - NEVER a slot grid, NEVER a
 * doctor picker, NEVER a fabricated selected_slot_id. The FULL lifecycle
 * (arrival / check-in / reschedule / cancel / no-show / fulfilled) already lives
 * in the lead-detail appointment workbench, so this cockpit LINKS there rather
 * than duplicating it. And the literal pipeline stage (leads.enquiry_status) is
 * NOT projected onto the lead read, so this cockpit shows the lifecycle-hop
 * signals the read DOES carry and says so - it never invents a "current stage".
 * These deferrals are stated once in LEAD_COCKPIT_CEILING_NOTE + BOOKING_CEILING_NOTE.
 *
 * NO OUTBOUND: this cockpit sends no WhatsApp / SMS - confirmation + reminder
 * sends are a later flag-gated package.
 *
 * COMPLIANCE: agent/COC are non-clinical roles. This cockpit reaches for NO
 * clinical field (clinical_note / findings_diagnosis / chief_complaint) - it
 * renders a PHI-safe lead worklist + administrative appointment status only.
 */

import type { DashboardMe } from '@/dashboard/types';
import type { FollowupRow } from '@/walkin/WalkinFollowupsPanel';
import type {
  Lead360Input,
  Lead360AppointmentCardView,
} from '@/dashboard/owner/lead-360-view';
import type {
  AdvanceLeadStageResult,
  BookedAppointmentResult,
  LeadStageTarget,
} from '@/dashboard/api';

// Reuse the Workboard's front-half stage ladder + error helpers verbatim (the
// same POST /api/leads/:id/stage contract) rather than re-deriving them. Import
// locally (CALL_CENTER_STAGE_TARGETS is derived from the ladder below) and
// re-export for downstream consumers.
import {
  FRONT_HALF_STAGE_TARGETS,
  stageTargetLabel,
  isConflictError,
  type StageTargetOption,
} from '@/dashboard/workboard/workboard-view';
export { FRONT_HALF_STAGE_TARGETS, stageTargetLabel, isConflictError };
export type { StageTargetOption };

// --- Role-only access decision (mirrors decideWorkboardAccess) --------------

/**
 * The roles that may OPEN the lead-to-appointment cockpit / command center: the
 * front-half operator pair that WORKS it - the front-desk agent (receptionist)
 * and the COC, the same pair that owns the Workboard - PLUS the leadership tiers
 * (owner, gm_agm, regional_head) who may SEE the operator queue surfaces under
 * their jurisdiction (LEADERSHIP-QUEUE-VISIBILITY-V1, V2-P1b; founder decision
 * 2026-07-05).
 *
 * SEE-NOT-DO: this set is a READ gate ONLY - it decides who the route renders
 * for, never what they may DO. Every write affordance stays keyed off its own
 * /me signal + backend route auth, independent of this set: Claim on the
 * operator-tier leadAssignmentClaimEnabled (CLAIM_CAPABLE_ROLES = receptionist /
 * coc; leadership is NOT claim-capable -> resolveClaimAffordance returns 'none',
 * no Claim button), Assign on the leadership-tier canAssign (the pre-existing
 * V2-P0 ASSIGN_CAPABLE authority), Qualify on the lead-stage engine (leadership
 * -> honest 401, the DELTA-A path). A role-only gate, keyed off the
 * server-authenticated me.role: NO /me flag, NO feature flag (internal surface,
 * default-on per ADR-0016). Backend route auth (the appointment / stage /
 * work-action / assignment role gates + scope) remains the real security
 * boundary.
 */
export const LEAD_COCKPIT_ROLES: ReadonlySet<DashboardMe['role']> =
  new Set<DashboardMe['role']>([
    'receptionist',
    'coc',
    'owner',
    'gm_agm',
    'regional_head',
    // Blended-team call center (ROLE-TOKEN-CALLCENTER-AGENT-V1): the cockpit
    // route renders their command center (CallcenterCommandCenter) via the
    // intake signal. Frontend twin of backend CALLCENTER_QUEUE_AUDIENCE_ROLES.
    'call_center_agent',
  ]);

export type LeadCockpitAccessDecision =
  | { kind: 'wait' }
  | { kind: 'render' }
  | { kind: 'redirect'; to: string };

/**
 * Pure access decision for the cockpit route guard. Mirrors
 * decideWorkboardAccess: wait while loading; redirect to /login on error or no
 * scope; redirect to the caller's own homePath when the role is outside the
 * cockpit set; otherwise render.
 */
export function decideLeadCockpitAccess(state: {
  loading: boolean;
  error: string | null;
  me: DashboardMe | null;
}): LeadCockpitAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) {
    return { kind: 'redirect', to: '/login' };
  }
  if (!LEAD_COCKPIT_ROLES.has(state.me.role)) {
    return { kind: 'redirect', to: state.me.homePath };
  }
  return { kind: 'render' };
}

// --- Lead worklist (grouped from the always-on work-actions feed) -----------

/**
 * One lead in the cockpit worklist - a distinct lead the caller has at least one
 * OPEN follow-up on. Built ONLY from feed fields: the carved `name` (first
 * non-empty patientName, never fabricated), the count of the lead's open
 * follow-ups, and the oldest open createdAt (a neutral age anchor, not a due
 * date). The fixed `channel`/`dueContext` constants are deliberately dropped.
 */
export interface LeadWorklistEntry {
  readonly leadId: number;
  /** Carved lead name; '' when the feed carried none (render 'Unknown patient'). */
  readonly name: string;
  readonly followupCount: number;
  readonly oldestCreatedAt: string | null;
}

/** Earlier of two nullable ISO timestamps (a null is treated as "no anchor"). */
function earlierIso(a: string | null, b: string | null): string | null {
  if (a === null) return b;
  if (b === null) return a;
  return Date.parse(a) <= Date.parse(b) ? a : b;
}

/**
 * Group the caller's open follow-up rows into one entry per distinct lead,
 * oldest-first (mirrors the feed's oldest-first ordering). Pure. A lead with
 * several open follow-ups collapses to ONE worklist row carrying the count.
 */
export function buildLeadWorklist(
  rows: readonly FollowupRow[],
): LeadWorklistEntry[] {
  const byLead = new Map<number, LeadWorklistEntry>();
  for (const r of rows) {
    const prev = byLead.get(r.leadId);
    if (prev === undefined) {
      byLead.set(r.leadId, {
        leadId: r.leadId,
        name: r.patientName || '',
        followupCount: 1,
        oldestCreatedAt: r.createdAt,
      });
    } else {
      byLead.set(r.leadId, {
        leadId: r.leadId,
        // Keep the first non-empty name; never overwrite a real name with ''.
        name: prev.name || r.patientName || '',
        followupCount: prev.followupCount + 1,
        oldestCreatedAt: earlierIso(prev.oldestCreatedAt, r.createdAt),
      });
    }
  }
  return Array.from(byLead.values()).sort((a, b) => {
    const at = a.oldestCreatedAt === null ? Infinity : Date.parse(a.oldestCreatedAt);
    const bt = b.oldestCreatedAt === null ? Infinity : Date.parse(b.oldestCreatedAt);
    if (at !== bt) return at - bt;
    return a.leadId - b.leadId;
  });
}

/** The selected lead's OPEN follow-up rows (filter the caller's feed by leadId). */
export function leadFollowups(
  rows: readonly FollowupRow[],
  leadId: number,
): FollowupRow[] {
  return rows.filter((r) => r.leadId === leadId);
}

/** Truthful worklist count label ("1 lead" / "N leads"). */
export function leadCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'lead' : 'leads'}`;
}

// --- Lead directory enrichment (from the flag-gated workspace read) ---------

/**
 * The workspace record for a given numeric leadId, or null. The workspace read
 * keys leads by `safeLeadId` = String(leads.id), so the lookup stringifies.
 * Returns null when the directory read is disabled/failed (records null) or the
 * lead is not in the caller's returned scope - the caller then shows the honest
 * "not loaded" enrichment state, never a fabricated record.
 */
export function findWorkspaceRecord(
  records: readonly Lead360Input[] | null,
  leadId: number,
): Lead360Input | null {
  if (records === null) return null;
  return records.find((r) => r.safeLeadId === String(leadId)) ?? null;
}

/** Stored statuses that count as an ACTIVE appointment (mirrors the backend
 *  ACTIVE_SCHEDULING_STATUSES / the one-active-per-lead booking guard). */
export const ACTIVE_STORED_STATUSES: readonly string[] = ['booked', 'confirmed'];

/**
 * The lead's single ACTIVE (booked/confirmed) appointment card, or null. Drives
 * the honest booking gate: when a lead already has an active appointment, the
 * cockpit does NOT offer a second "Book" (which the backend would 409 as
 * active_appointment_exists) - it points to the appointment workbench instead.
 * Reads ONLY the appointment section the workspace record carries; null when the
 * record/section is absent.
 */
export function activeAppointmentCard(
  record: Lead360Input | null,
): Lead360AppointmentCardView | null {
  const section = record?.appointmentSection;
  if (section === undefined || !section.hasAppointments) return null;
  return (
    section.cards.find((c) => ACTIVE_STORED_STATUSES.includes(c.storedStatus)) ??
    null
  );
}

/** True when the lead already has an active (booked/confirmed) appointment. */
export function hasActiveAppointment(record: Lead360Input | null): boolean {
  return activeAppointmentCard(record) !== null;
}

// LEAD-APPOINTMENT-CRM-COHERENCE-V1 (SCOPE 2 - ONE-PICKER LAW): the legacy
// hour/minute wall-clock helper functions were DELETED. The cockpit now chooses a
// time ONLY through the shared server-computed slot grid (DaySlotPicker), which
// returns a UTC instant directly (form.slotIso) - no client wall-clock math.

// --- Feedback + DI'd action orchestrators (node-testable without a click) ---

export interface CockpitFeedback {
  readonly kind: 'success' | 'error';
  readonly lines: readonly string[];
}

/** Pull an HTTP status off an unknown error without importing PortalApiError. */
function errorStatus(error: unknown): number | null {
  if (error !== null && typeof error === 'object' && 'status' in error) {
    const s = (error as { status?: unknown }).status;
    if (typeof s === 'number') return s;
  }
  return null;
}

/**
 * Human message for a failed BOOK. ERROR-PASSTHROUGH LAW
 * (LEAD-APPOINTMENT-CRM-COHERENCE-V1): render the backend's own PHI-free,
 * plain-language message VERBATIM for the booking-domain 400/409 failures (e.g.
 * "This slot is already booked...", "This lead already has an appointment - use
 * Reschedule...", "Outside clinic hours..."). The old code translated EVERY 409
 * to an active-appointment line, so a slot conflict was mistranslated (the
 * founder's E2). Every other status - including a 500 whose DatabaseError body is
 * a RAW driver string - uses the safe generic fallback (LAW 3a: no SQL leak).
 */
export function bookingErrorMessage(error: unknown): string {
  const status = errorStatus(error);
  if (
    (status === 400 || status === 409) &&
    error instanceof Error &&
    error.message.trim() !== '' &&
    !/^HTTP \d+/.test(error.message)
  ) {
    return error.message;
  }
  return 'Could not book the appointment. Please try again.';
}

/** Human message for a failed stage advance (409 -> reload prompt). */
export function stageErrorMessage(error: unknown): string {
  if (errorStatus(error) === 409) {
    return 'This lead was just updated by someone else. Refresh and try again.';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Could not move the lead stage. Please try again.';
}

export interface BookAppointmentDeps {
  readonly bookAppointment: (
    leadId: number,
    appointmentTime: string,
  ) => Promise<BookedAppointmentResult>;
}

export interface BookAppointmentInput {
  readonly leadId: number;
  /** A UTC ISO instant chosen from the shared server slot grid (form.slotIso). */
  readonly iso: string;
  /** The IST wall-clock label to echo back (display only). */
  readonly whenLabel: string;
}

export interface CockpitActionResult {
  readonly ok: boolean;
  readonly feedback: CockpitFeedback;
}

/**
 * Book ONE appointment for the lead at the chosen time. Never throws: resolves
 * ok=true + a success line on a created appointment, ok=false + the mapped error
 * line otherwise (the caller keeps the form so the operator can retry). Doctor
 * stays unassigned server-side - the success line says so, it never claims a
 * doctor or a slot.
 */
export async function bookLeadAppointment(
  deps: BookAppointmentDeps,
  input: BookAppointmentInput,
): Promise<CockpitActionResult> {
  try {
    // The created appointment is forced to status 'booked' with doctor_id null
    // server-side; we only need to know it succeeded, not echo the row back.
    await deps.bookAppointment(input.leadId, input.iso);
    return {
      ok: true,
      feedback: {
        kind: 'success',
        lines: [
          `Appointment booked for lead #${input.leadId} at ${input.whenLabel} (no doctor assigned yet).`,
          'Refresh to see the updated appointment status.',
        ],
      },
    };
  } catch (err) {
    return { ok: false, feedback: { kind: 'error', lines: [bookingErrorMessage(err)] } };
  }
}

export interface MoveStageDeps {
  readonly advanceLeadStage: (
    leadId: number,
    toStage: LeadStageTarget,
  ) => Promise<AdvanceLeadStageResult>;
}

export interface MoveStageInput {
  readonly leadId: number;
  readonly to: LeadStageTarget;
  /** Human label for the target stage (for the success line). */
  readonly label: string;
}

export interface MoveStageResult extends CockpitActionResult {
  /** True when the backend reported it closed the lead's open work item(s). */
  readonly workItemClosed: boolean;
}

/**
 * Advance the lead one front-half stage. Never throws: on success reports
 * ok=true + the committed target + whether the lead's open work item was closed
 * (so the caller can drop the cleared follow-ups honestly); on failure ok=false
 * + the mapped error line, workItemClosed=false.
 */
export async function moveLeadStage(
  deps: MoveStageDeps,
  input: MoveStageInput,
): Promise<MoveStageResult> {
  try {
    const res = await deps.advanceLeadStage(input.leadId, input.to);
    return {
      ok: true,
      workItemClosed: res.workItemClosed === true,
      feedback: {
        kind: 'success',
        lines: [`Lead #${input.leadId} moved to ${input.label}.`],
      },
    };
  } catch (err) {
    return {
      ok: false,
      workItemClosed: false,
      feedback: { kind: 'error', lines: [stageErrorMessage(err)] },
    };
  }
}

// --- Honest-ceiling copy (stated once; never a broken-looking control) ------

/**
 * The single, explicit cockpit ceiling note. States what v1 does (worklist +
 * book + stage move + clear follow-up in one place) AND what is deferred (full
 * slot selection, doctor assignment, outbound confirmation/reminder sends, the
 * full appointment lifecycle which lives in the appointment workbench) so the
 * ceiling is truthful rather than a broken-looking control.
 */
export const LEAD_COCKPIT_CEILING_NOTE =
  'This cockpit lists the leads with an open follow-up assigned to you and lets you book an appointment, move the lead stage, and clear the follow-up in one place - it is not the full lead directory (open Leads or the Workboard for that). The lead read does not expose the literal pipeline stage, so this cockpit shows the appointment status it does carry and lets you advance the stage - it never displays a fabricated current stage. Booking offers real open slots inside the clinic\'s working hours (a taken slot cannot be double-booked); doctor assignment is coming later, so the appointment books unassigned and there is no doctor picker. Confirmation and reminder messages are coming later too - this cockpit sends no WhatsApp or SMS. For the full appointment lifecycle (arrival, check-in, reschedule, cancel, no-show, fulfilled) open the lead in the appointment workbench.';

/** The booking-panel ceiling note, rendered next to the slot picker.
 *  STITCH-2 SCOPE 8: slots are now REAL (clinic hours + taken marks from
 *  the same config the booking route enforces). The remaining honest
 *  ceiling is doctor/chair assignment (the scheduling-module seam). */
export const BOOKING_CEILING_NOTE =
  'Slots show this clinic\'s real working hours; a taken slot cannot be booked twice. Doctor assignment is coming later - the appointment books unassigned at the slot you choose.';

/** The honest enrichment-unavailable note (workspace directory read disabled). */
export const ENRICHMENT_UNAVAILABLE_NOTE =
  'Source and appointment status are not loaded here - enable the Leads directory to see them. You can still book and move the lead stage below.';

/**
 * STAGE-ADVANCE-AUTHORITY-CONFIG-V1 (V2-P0d): the honest reduced-capability note
 * for the call_center_agent role on this legacy cockpit, updated as V2-P0d
 * UN-REDUCES the parts it unblocks. QUALIFY is now config-authorized for the
 * agent (Partha Q5: qualified -> { receptionist, call_center_agent }, enforced
 * config-consulted in api/routes/lead-stage.ts) and the full lead record now
 * drills (LEADS_WORKSPACE_ROLES widened) -- BOTH are shown + work. What STAYS
 * reduced is BOOKING (the appointment create/slot flow is not in
 * APPOINTMENT_CREATE_ROLES for this role -- DELTA-6, carried by V2-P2), plus the
 * still-401 stage targets Booked / Arrived / Lost (receptionist/coc-owned, not
 * granted to the agent). So the note now states only the honest booking seam.
 */
export const CALL_CENTER_REDUCED_NOTE =
  'Call-center view: qualify the leads assigned to you, clear their follow-ups, and open the full lead record from here. Booking the appointment from this screen is not enabled for the call-center role yet - the front desk books today; that lands in a later release.';

/**
 * STAGE-ADVANCE-AUTHORITY-CONFIG-V1 (V2-P0d): the stage-advance targets shown to
 * the call_center_agent on the reduced legacy cockpit -- QUALIFY ONLY. The seed
 * (Partha Q5) grants the agent authority over `qualified` alone; the other
 * front-half targets (Booked / Arrived = receptionist-owned booking flow;
 * Lost = coc-owned) would 401, so they are not offered here. Derived from the
 * shared ladder so the label stays in lockstep; never a re-typed vocabulary.
 */
export const CALL_CENTER_STAGE_TARGETS: readonly StageTargetOption[] =
  FRONT_HALF_STAGE_TARGETS.filter((t) => t.to === 'qualified');
